import { Shader } from "./core/shaderUtils";
import { GTexturePool } from "./core/texturePool";
import { GStack, GetVec2, GetVec3, Matrix4x4, Vector3 } from "./core/types";

import { GLBindUniformBuffer, GLSetTexture, GLSetVec3 } from "./helpers/glHelper";
import { SceneBase } from "./scenes/scene";
import { GetShaderSourceIntegrateSpecularBRDFPS } from "./shaders/shaderBasePass";
import { ShaderSourceFullscreenPassVS } from "./shaders/shaderPostProcess";
import { CommonRenderingResources } from "./shaders/shaderConfig";

import { CreateFramebufferWithAttachment, CreateTextureRT } from "./core/resourcesUtils";
import { GPostProcessPasses } from "./renderingMain";
import { GMeshLoader } from "./helpers/meshLoader";
import { GMeshGenerator } from "./helpers/meshGenerator";
import { SDFRender } from "./scenes/sdf";

export function GetBasePassUniformParametersList(gl: WebGL2RenderingContext, shaderProgram: WebGLProgram) {
    const params = {
        PreTranslate: gl.getUniformLocation(shaderProgram, "PreTranslate"),
        LocalToWorldMatrix: gl.getUniformLocation(shaderProgram, "LocalToWorldMatrix"),
        WorldToViewMatrix: gl.getUniformLocation(shaderProgram, "WorldToViewMatrix"),

        AlbedoTexture: gl.getUniformLocation(shaderProgram, "AlbedoTexture"),
        NormalTexture: gl.getUniformLocation(shaderProgram, "NormalTexture"),
        RoughnessTexture: gl.getUniformLocation(shaderProgram, "RoughnessTexture"),
        ProjectorTexture: gl.getUniformLocation(shaderProgram, "ProjectorTexture"),
        TextureScale: gl.getUniformLocation(shaderProgram, "TextureScale"),
        Brightness: gl.getUniformLocation(shaderProgram, "Brightness"),
        Metalness: gl.getUniformLocation(shaderProgram, "Metalness"),
        CurRoughness: gl.getUniformLocation(shaderProgram, "CurRoughness"),
        CubemapTexture: gl.getUniformLocation(shaderProgram, "CubemapTexture"),
        SrcMip: gl.getUniformLocation(shaderProgram, "SrcMip"),
        SphericalTexture: gl.getUniformLocation(shaderProgram, "SphericalTexture"),
        BRDF_LUT: gl.getUniformLocation(shaderProgram, "BRDF_LUT"),
        SDFTexture: gl.getUniformLocation(shaderProgram, "SDFTexture"),
        SDFTexture2: gl.getUniformLocation(shaderProgram, "SDFTexture2"),

        MaskTexture: gl.getUniformLocation(shaderProgram, "MaskTexture"),
        RandNumSeed: gl.getUniformLocation(shaderProgram, "RandNumSeed"),

        PrevFrameSceneColor: gl.getUniformLocation(shaderProgram, "PrevFrameSceneColor"),
        PrevFrameSceneColorBlur: gl.getUniformLocation(shaderProgram, "PrevFrameSceneColorBlur"),

        SHCoefficientsTexture: gl.getUniformLocation(shaderProgram, "SHCoefficientsTexture"),

        EUseLightprobe: gl.getUniformLocation(shaderProgram, "EUseLightprobe"),
        LightprobeIndex: gl.getUniformLocation(shaderProgram, "LightprobeIndex"),
        bEmitter: gl.getUniformLocation(shaderProgram, "bEmitter"),
        bHasMask: gl.getUniformLocation(shaderProgram, "bHasMask"),
        bHasMaskFromLuminance: gl.getUniformLocation(shaderProgram, "bHasMaskFromLuminance"),
        LightprobesGridSize: gl.getUniformLocation(shaderProgram, "LightprobesGridSize"),

        Normal: gl.getUniformLocation(shaderProgram, "Normal"),
        Tangent: gl.getUniformLocation(shaderProgram, "Tangent"),

        LineStart: gl.getUniformLocation(shaderProgram, "LineStart"),
        LineEnd: gl.getUniformLocation(shaderProgram, "LineEnd"),
        LineScale: gl.getUniformLocation(shaderProgram, "LineScale"),

        RopePositionsTexture: gl.getUniformLocation(shaderProgram, "RopePositionsTexture"),
        RopePositionsTextureSDF: gl.getUniformLocation(shaderProgram, "RopePositionsTextureSDF"),

        //For particles
        ParticleCurSpawnIndexRange: gl.getUniformLocation(shaderProgram, "ParticleCurSpawnIndexRange"),
        TileHealthStateTexture: gl.getUniformLocation(shaderProgram, "TileHealthStateTexture"),

        //Destr Paint
        FireTexture: gl.getUniformLocation(shaderProgram, "FireTexture"),
        FuelTexture: gl.getUniformLocation(shaderProgram, "FuelTexture"),
        PaintBrightnessScale: gl.getUniformLocation(shaderProgram, "PaintBrightnessScale"),

        SceneUniformBuffer: gl.getUniformBlockIndex(shaderProgram, "SceneUniformBuffer"),
    };
    return params;
}

export class Material {
    bisEmmiter = false;
    bHasMask = false;
    bHasMaskFromLuminance = false;
    bAdditiveBlend = false;
    Brightness = 1.0;
    Metalness = 0.0;
    TextureScale = 1.0;

    AlbedoTexture: WebGLTexture | null = null;
    NormalTexture: WebGLTexture | null = null;
    RoughnessTexture: WebGLTexture | null = null;
    MaskTexture: WebGLTexture | null = null;

    CopyConstruct(inMaterial: Material) {
        this.bisEmmiter = inMaterial.bisEmmiter;
        this.bAdditiveBlend = inMaterial.bAdditiveBlend;
        this.Brightness = inMaterial.Brightness;
        this.Metalness = inMaterial.Metalness;
        this.TextureScale = inMaterial.TextureScale;

        this.AlbedoTexture = inMaterial.AlbedoTexture;
        this.NormalTexture = inMaterial.NormalTexture;
        this.RoughnessTexture = inMaterial.RoughnessTexture;
    }

    InitTextures(
        gl: WebGL2RenderingContext,
        albedo: string,
        normal: string | null,
        roughness: string | null,
        bEmitter = false,
        customTexturePath: string | null = null,
    ) {
        this.bisEmmiter = bEmitter;
        this.AlbedoTexture = GTexturePool.CreateTexture(gl, false, albedo, true, true, false, null, customTexturePath);
        if (normal) {
            this.NormalTexture = GTexturePool.CreateTexture(
                gl,
                false,
                normal,
                true,
                true,
                false,
                null,
                customTexturePath,
            );
        }
        if (roughness) {
            this.RoughnessTexture = GTexturePool.CreateTexture(
                gl,
                false,
                roughness,
                true,
                true,
                false,
                null,
                customTexturePath,
            );
        }
    }
}

export class BoundingBoxAA {
    Position = GetVec3(0, 0, 0);
    Extent = GetVec3(1, 1, 1);
}

export const BasePassVertexAttributeLocationList = {
    VertexBuffer: 0,
    TexCoordsBuffer: 1,
    NormalsBuffer: 2,
    TangentsBuffer: 3,
};

export class Mesh {
    Name: string | null = null;
    bCanDraw = false;
    Position = GetVec3(0, 0, 0);
    Orientation = GetVec3(0, 0, 0);
    Scale = GetVec3(1, 1, 1);
    bNormalizeScaleAllAxes = false;
    bNormalizeScale = false;
    bSetOriginAtCenter = true;

    Material = new Material();

    LocalToWorldMatrix = new Matrix4x4();

    IndexBufferElementType: GLenum = WebGL2RenderingContext.UNSIGNED_SHORT;
    IndexBufferGPU: WebGLBuffer | null = null;
    VertexBufferGPU: WebGLBuffer | null = null;
    TexCoordsBufferGPU: WebGLBuffer | null = null;
    NormalsBufferGPU: WebGLBuffer | null = null;
    TangentsBufferGPU: WebGLBuffer | null = null;
    VAO: WebGLVertexArrayObject | null = null;

    GeometryBufferCPU: Float32Array | null = null; //Raw Vertex Buffer (no indirection)

    NumVertices = 0;
    NumIndices = 0;

    InitPositionMin: Vector3 | null = null;
    InitPositionMax: Vector3 | null = null;
    InitBBoxCenter = new Vector3(0, 0, 0);
    InitBBoxExtent: Vector3 | null = null;
    InitBBoxExtentNorm: Vector3 | null = null;

    SDFTexture3D: WebGLTexture | null = null;

    static GMeshesLocationFolder = "assets/meshes/";

    RegenerateMatrix() {
        const curScale = GStack.TempVec3_2;
        curScale.Set(this.Scale);
        if (this.bNormalizeScale && this.InitBBoxExtent !== null) {
            const scaleIfNotZero = (p: number, div: number): number => {
                if (div > 0.00001) {
                    return p / div;
                } else {
                    return p;
                }
            };
            //
            const maxExtent = Math.max(this.InitBBoxExtent.z, Math.max(this.InitBBoxExtent.x, this.InitBBoxExtent.y));
            curScale.x = scaleIfNotZero(curScale.x, maxExtent);
            curScale.y = scaleIfNotZero(curScale.y, maxExtent);
            curScale.z = scaleIfNotZero(curScale.z, maxExtent);
        } else if (this.bNormalizeScaleAllAxes && this.InitBBoxExtent !== null) {
            const scaleIfNotZero = (p: number, div: number): number => {
                if (div > 0.01) {
                    return p / div;
                } else {
                    return p;
                }
            };
            curScale.x = scaleIfNotZero(curScale.x, this.InitBBoxExtent.x);
            curScale.y = scaleIfNotZero(curScale.y, this.InitBBoxExtent.y);
            curScale.z = scaleIfNotZero(curScale.z, this.InitBBoxExtent.z);
        }

        Matrix4x4.ConstructLocalToWorldMatrix(this.Position, this.Orientation, curScale, this.LocalToWorldMatrix);
        this.LocalToWorldMatrix.serializeForGPUImport();
    }

    LoadMesh(
        gl: WebGL2RenderingContext,
        path: string,
        meshIndex: number,
        preTransformMat: Matrix4x4 | null = null,
        bGenerateSDFTexture = false,
    ) {
        if (this.Name == null) {
            this.Name = /* ExtractFileNameFromPath */ path;
        }
        this.InitBuffersGPU(gl);
        GMeshLoader.Load(gl, this, path, meshIndex, preTransformMat, bGenerateSDFTexture);
    }

    InitBuffersGPU(gl: WebGL2RenderingContext) {
        this.VertexBufferGPU = gl.createBuffer();
        this.TexCoordsBufferGPU = gl.createBuffer();
        this.NormalsBufferGPU = gl.createBuffer();
    }

    GeneratePlane(gl: WebGL2RenderingContext) {
        GMeshGenerator.GeneratePlane(gl, this);
    }

    GenerateVAO(gl: WebGL2RenderingContext) {
        this.VAO = gl.createVertexArray();
        gl.bindVertexArray(this.VAO);

        //Index Buffer
        if (this.NumIndices > 0) {
            gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.IndexBufferGPU);
        }

        //Vertex Buffer Bind
        //bind resource to this attribute
        gl.bindBuffer(gl.ARRAY_BUFFER, this.VertexBufferGPU);
        gl.enableVertexAttribArray(BasePassVertexAttributeLocationList.VertexBuffer); //turn on attribute
        gl.vertexAttribPointer(
            BasePassVertexAttributeLocationList.VertexBuffer,
            3,
            gl.FLOAT,
            false,
            3 * Float32Array.BYTES_PER_ELEMENT,
            0,
        );

        //TexCoords Buffer Bind
        //bind resource to this attribute
        gl.bindBuffer(gl.ARRAY_BUFFER, this.TexCoordsBufferGPU);
        gl.enableVertexAttribArray(BasePassVertexAttributeLocationList.TexCoordsBuffer); //turn on attribute
        gl.vertexAttribPointer(
            BasePassVertexAttributeLocationList.TexCoordsBuffer,
            2,
            gl.FLOAT,
            false,
            2 * Float32Array.BYTES_PER_ELEMENT,
            0,
        );

        //Normals
        gl.bindBuffer(gl.ARRAY_BUFFER, this.NormalsBufferGPU);
        gl.enableVertexAttribArray(BasePassVertexAttributeLocationList.NormalsBuffer); //turn on attribute
        gl.vertexAttribPointer(
            BasePassVertexAttributeLocationList.NormalsBuffer,
            3,
            gl.FLOAT,
            false,
            3 * Float32Array.BYTES_PER_ELEMENT,
            0,
        );

        //Tangents
        gl.bindBuffer(gl.ARRAY_BUFFER, this.TangentsBufferGPU);
        gl.enableVertexAttribArray(BasePassVertexAttributeLocationList.TangentsBuffer); //turn on attribute
        gl.vertexAttribPointer(
            BasePassVertexAttributeLocationList.TangentsBuffer,
            4,
            gl.FLOAT,
            false,
            4 * Float32Array.BYTES_PER_ELEMENT,
            0,
        );
    }
}

export let RopePositionsTexture: WebGLTexture | null = null;

export function CreateRopePositionsTexture(gl: WebGL2RenderingContext) {
    RopePositionsTexture = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, RopePositionsTexture);

    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    gl.texImage2D(gl.TEXTURE_2D, 0, gl.R32F, 1024, 1, 0, gl.RED, gl.FLOAT, null);
}

export class GBasePass {
    static GInstance: GBasePass | null = null;

    ShaderIntegrateBRDF;
    TextureIntegratedBRDF;
    FBOIntegratedBRDF;
    IntegratedBRDFTextureSize = 128;

    constructor(gl: WebGL2RenderingContext) {
        this.ShaderIntegrateBRDF = new Shader(
            gl,
            ShaderSourceFullscreenPassVS,
            GetShaderSourceIntegrateSpecularBRDFPS(),
            GetBasePassUniformParametersList,
        );

        //Compute BRDF
        this.TextureIntegratedBRDF = CreateTextureRT(
            gl,
            GetVec2(this.IntegratedBRDFTextureSize, this.IntegratedBRDFTextureSize),
            gl.RG16F,
            gl.RG,
            gl.HALF_FLOAT,
            false,
        );
        this.FBOIntegratedBRDF = CreateFramebufferWithAttachment(gl, this.TextureIntegratedBRDF!);
        this.IntegrateBRDF(gl);
    }

    Begin(gl: WebGL2RenderingContext, curShader: Shader) {
        /* gl.enable(gl.BLEND);
        gl.blendFunc(gl.ONE, gl.ONE);
        gl.blendEquation(gl.MAX); */

        gl.useProgram(curShader.ShaderProgram);

        const uniformLoc = curShader.UniformParametersLocationList;

        GLBindUniformBuffer(
            gl,
            SceneBase.UniformBuffer.BufferGPU!,
            curShader.ShaderProgram,
            uniformLoc.SceneUniformBuffer,
        );

        gl.enable(gl.DEPTH_TEST);
        gl.depthFunc(gl.LESS);

        // Enable back face culling
        gl.enable(gl.CULL_FACE);
        gl.cullFace(gl.FRONT);
    }

    End(gl: WebGL2RenderingContext) {
        //gl.disable(gl.BLEND);
        //Depth
        gl.disable(gl.DEPTH_TEST);

        // Disable back face culling
        gl.disable(gl.CULL_FACE);
    }

    RenderMesh(
        gl: WebGL2RenderingContext,
        curShader: Shader,
        inMesh: Mesh,
        envMap: WebGLTexture | null = null,
        EUseLightprobe = 0,
        lightprobeIndex = 0,
    ) {
        if (!inMesh.bCanDraw) {
            return;
        }

        if (inMesh.Material.bAdditiveBlend) {
            gl.enable(gl.BLEND);
            gl.blendFunc(gl.ONE, gl.ONE);
            gl.blendEquation(gl.FUNC_ADD);
        }
        if (inMesh.Material.bHasMask) {
            gl.enable(gl.BLEND);
            gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
            gl.blendEquation(gl.FUNC_ADD);
            gl.depthMask(false);
        }

        const uniformsLocation = curShader.UniformParametersLocationList;

        gl.bindVertexArray(inMesh.VAO);

        gl.uniformMatrix4fv(uniformsLocation.LocalToWorldMatrix, false, inMesh.LocalToWorldMatrix.elementsSerialized);
        gl.uniformMatrix4fv(
            uniformsLocation.WorldToViewMatrix,
            false,
            SceneBase.WorldToCameraSpaceMatCPU.elementsSerialized,
        );
        /* const preT = GStack.TempVec3;
		preT.Set3(0,0,0);
		if(inMesh.bSetOriginAtCenter)
		{
			preT.Set(inMesh.InitBBoxCenter);
		} */
        gl.uniform1i(uniformsLocation.LightprobesGridSize, SceneBase.LightprobesManager.NumProbes.x);

        GLSetVec3(gl, uniformsLocation.PreTranslate, inMesh.InitBBoxCenter);
        gl.uniform1f(uniformsLocation.TextureScale, inMesh.Material.TextureScale);
        gl.uniform1f(uniformsLocation.Brightness, inMesh.Material.Brightness);
        gl.uniform1f(uniformsLocation.Metalness, inMesh.Material.Metalness);
        gl.uniform1i(uniformsLocation.bEmitter, inMesh.Material.bisEmmiter ? 1 : 0);
        gl.uniform1i(uniformsLocation.bHasMask, inMesh.Material.bHasMask ? 1 : 0);
        gl.uniform1i(uniformsLocation.bHasMaskFromLuminance, inMesh.Material.bHasMaskFromLuminance ? 1 : 0);

        let textureBindIndex = 1;
        GLSetTexture(gl, uniformsLocation.AlbedoTexture, inMesh.Material.AlbedoTexture, textureBindIndex++);
        GLSetTexture(gl, uniformsLocation.NormalTexture, inMesh.Material.NormalTexture, textureBindIndex++);
        GLSetTexture(gl, uniformsLocation.RoughnessTexture, inMesh.Material.RoughnessTexture, textureBindIndex++);

        GLSetTexture(
            gl,
            uniformsLocation.PrevFrameSceneColor,
            GPostProcessPasses.Bloom!.DownsampleRTMipArr![1],
            textureBindIndex++,
        );
        GLSetTexture(
            gl,
            uniformsLocation.PrevFrameSceneColorBlur,
            GPostProcessPasses.Bloom!.DownsampleRTMipArr![2],
            textureBindIndex++,
        );

        //																	MOVE TO SCENE SPECIFIC BASE PASS
        if (SceneBase.GpCurProjectorTexture) {
            GLSetTexture(
                gl,
                uniformsLocation.ProjectorTexture,
                //SceneBase.ProjectorTexture.InnerTexture,
                //GVectorField.GInstance!.GetCurTextureDensity(),
                SceneBase.GpCurProjectorTexture,
                textureBindIndex++,
            );
        }

        if (envMap) {
            gl.activeTexture(gl.TEXTURE0 + textureBindIndex);
            gl.bindTexture(gl.TEXTURE_CUBE_MAP, envMap);
            gl.uniform1i(uniformsLocation.CubemapTexture, textureBindIndex);
            textureBindIndex++;
        }
        GLSetTexture(gl, uniformsLocation.BRDF_LUT, this.TextureIntegratedBRDF, textureBindIndex++);
        gl.uniform1i(uniformsLocation.EUseLightprobe, EUseLightprobe);
        gl.uniform1i(uniformsLocation.LightprobeIndex, lightprobeIndex);
        GLSetTexture(
            gl,
            curShader.UniformParametersLocationList.SHCoefficientsTexture,
            SceneBase.LightprobesManager.TextureSHCoeff,
            textureBindIndex++,
        );

        if (SDFRender.GpSDFMeshTexture) {
            gl.activeTexture(gl.TEXTURE0 + textureBindIndex);
            gl.bindTexture(gl.TEXTURE_3D, SDFRender.GpSDFMeshTexture);
            gl.uniform1i(curShader.UniformParametersLocationList.SDFTexture, textureBindIndex);
            textureBindIndex++;
        }
        if (SDFRender.GpSDFMeshTexture2) {
            gl.activeTexture(gl.TEXTURE0 + textureBindIndex);
            gl.bindTexture(gl.TEXTURE_3D, SDFRender.GpSDFMeshTexture2);
            gl.uniform1i(curShader.UniformParametersLocationList.SDFTexture2, textureBindIndex);
            textureBindIndex++;
        }

        if (RopePositionsTexture) {
            GLSetTexture(gl, uniformsLocation.RopePositionsTextureSDF, RopePositionsTexture, textureBindIndex++);
        }

        if (inMesh.NumIndices > 0) {
            gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, inMesh.IndexBufferGPU);
            gl.drawElements(gl.TRIANGLES, inMesh.NumIndices, inMesh.IndexBufferElementType, 0);
            //gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, null);
        } else {
            gl.drawArrays(gl.TRIANGLES, 0, inMesh.NumVertices);
        }

        if (inMesh.Material.bAdditiveBlend || inMesh.Material.bHasMask) {
            gl.disable(gl.BLEND);
            if (inMesh.Material.bHasMask) {
                gl.depthMask(true);
            }
        }
    }

    IntegrateBRDF(gl: WebGL2RenderingContext) {
        const curShader = this.ShaderIntegrateBRDF;
        gl.viewport(0, 0, this.IntegratedBRDFTextureSize, this.IntegratedBRDFTextureSize);
        gl.bindFramebuffer(gl.FRAMEBUFFER, this.FBOIntegratedBRDF);

        gl.useProgram(curShader.ShaderProgram);
        gl.bindVertexArray(CommonRenderingResources.FullscreenPassVAO);

        GLBindUniformBuffer(
            gl,
            SceneBase.UniformBuffer.BufferGPU!,
            curShader.ShaderProgram,
            curShader.UniformParametersLocationList.SceneUniformBuffer,
        );

        gl.drawArrays(gl.TRIANGLES, 0, 3);
    }
}
